import { TeacherMetricCard, TeacherSectionHeader, TeacherStudentIdentity, TeacherStudentResultRow, TeacherAssessmentHistoryRow } from '../../design/TeacherComponents';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import FormControl from '@mui/material/FormControl';
import InputAdornment from '@mui/material/InputAdornment';
import LinearProgress from '@mui/material/LinearProgress';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/Add';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import BarChartOutlinedIcon from '@mui/icons-material/BarChartOutlined';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import EventOutlinedIcon from '@mui/icons-material/EventOutlined';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import StudentAvatarView from '../../shared/StudentAvatarView';
import { fetchJson, getApiBase } from '../../apiBase';
import { notificationCounts } from '../messages/NotificationStatusChip';
import { buildClassPulse, classPulseDate, classPulseFormat, withinAssessmentPeriod } from './classPulseMetrics';
import ClassPulseStudentDrawer from './ClassPulseStudentDrawer';
import ClassPulseSubjectDrawer from './ClassPulseSubjectDrawer';
import { buildClassPulseSubjectDetail } from './classPulseSubjectDetail';
import { CourseGlyph, getCourseVisual } from './ClassPulseVisualTokens';
import { teacherTypography } from '../../design/typography';
import DevelopmentAssessmentDistribution from './DevelopmentAssessmentDistribution';

const requestUrls = (school) => {
  const base = `${getApiBase()}/admin`;
  return {
    classes: `${base}/classes/${encodeURIComponent(school)}`,
    results: `${base}/course-assessments/${encodeURIComponent(school)}/results`,
    avatarRoster: `${base}/points/${encodeURIComponent(school)}/students`,
    students: (classId) => `${base}/students/${encodeURIComponent(school)}/by-class/${encodeURIComponent(classId)}`,
  };
};

const surface = { bgcolor: '#fff', border: '1px solid #e8eaf3', borderRadius: '14px', boxShadow: 'none' };
const actionSx = { minHeight: 44, ...teacherTypography.action,
  '&.Mui-focusVisible': { outline: '3px solid #3a2f85', outlineOffset: 2 } };
const selectTouchSx = {
  minHeight: 44,
  '& .MuiSelect-select': {
    boxSizing: 'border-box', display: 'flex', alignItems: 'center',
    minHeight: '44px !important', py: '0 !important',
  },
};

function EmptyState({ children, title }) {
  return (
    <Paper variant="outlined" sx={{ p: { xs: 3, md: 5 }, textAlign: 'center', borderRadius: 3 }}>
      <Typography variant="subtitle1" sx={teacherTypography.cardTitle}>{title}</Typography>
      <Typography variant="body2" sx={{ ...teacherTypography.supportingText, mt: 1 }}>{children}</Typography>
    </Paper>
  );
}

const KpiCard = TeacherMetricCard;

function SubjectCard({ subject, onSelect }) {
  const visual = getCourseVisual(subject.label);
  const valid = subject.average !== null;
  return (
    <Paper component="button" type="button" onClick={() => onSelect(subject.key)}
      aria-label={`${subject.label} dersinin geçmiş sınavlarını aç`}
      variant="outlined" sx={{ p: 2.1, borderRadius: '14px', minWidth: 0, width: '100%',
      display: 'grid', gridTemplateColumns: 'minmax(0,1fr) auto', columnGap: 1, alignItems: 'center', textAlign: 'left', cursor: 'pointer', font: 'inherit', color: 'inherit',
      borderColor: '#e5e8f2', boxShadow: '0 5px 18px rgba(67,55,125,.025)',
      background: 'linear-gradient(120deg, #fff, #fdfcff)',
      transition: 'border-color .15s, box-shadow .15s',
      '&:hover': { borderColor: '#a996ed', boxShadow: '0 10px 24px rgba(67,55,125,.10)' },
      '&:focus-visible': { outline: '3px solid #302479', outlineOffset: 2 } }}>
      <Stack direction="row" spacing={1.1} alignItems="center">
        <CourseGlyph name={subject.label} size={39} />
        <Typography variant="subtitle2" sx={{ ...teacherTypography.cardTitle, overflowWrap: 'anywhere', minWidth: 0 }}>{subject.label}</Typography>
      </Stack>
      <Typography variant="h5" component="p" sx={{ ...teacherTypography.kpiValue, mt: 0, fontSize: 22, textAlign: 'right', fontVariantNumeric: 'tabular-nums',
        color: '#26204e' }}>
        {!valid && subject.developmentResults ? 'Düzey' : classPulseFormat(subject.average)}
        {valid && <Typography component="span" variant="caption" sx={teacherTypography.scoreUnit}> /100</Typography>}
      </Typography>
      {!(subject.developmentResults && !valid) && <LinearProgress
        variant="determinate" value={valid ? Math.max(0, Math.min(100, subject.average)) : 0}
        aria-label={`${subject.label} ortalama puanı`}
        sx={{ gridColumn: '1 / -1', mt: 1.4, height: 6, borderRadius: 8, bgcolor: '#efedf5',
          '& .MuiLinearProgress-bar': { borderRadius: 8, bgcolor: visual.line } }}
      />}
      <Typography variant="caption" sx={{ ...teacherTypography.metadata, gridColumn: '1 / -1', mt: 1.1, display: 'block' }}>
        {subject.results} puanlı sonuç · {subject.students} öğrenci
      </Typography>
      {subject.developmentResults > 0 && <Typography variant="caption" sx={{ ...teacherTypography.metadata, gridColumn: '1 / -1' }}>
        Gelişim Düzeyi: {subject.developmentResults} kayıt · {subject.developmentStudents} öğrenci
      </Typography>}
    </Paper>
  );
}

export default function ClassPulseDashboard({ schoolId, onError, onCreateAssessment }) {
  const urls = useMemo(() => requestUrls(schoolId), [schoolId]);
  const [classes, setClasses] = useState([]);
  const [results, setResults] = useState([]);
  const [roster, setRoster] = useState([]);
  const [avatarSelections, setAvatarSelections] = useState({});
  const [classId, setClassId] = useState('');
  const [period, setPeriod] = useState('all');
  const [examFilter, setExamFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState('alpha');
  const [studentId, setStudentId] = useState(null);
  const [subjectId, setSubjectId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingRoster, setLoadingRoster] = useState(false);
  const [rosterError, setRosterError] = useState(false);
  const [loadError, setLoadError] = useState('');

  const load = useCallback(async () => {
    if (!schoolId) return;
    setLoading(true);
    setLoadError('');
    try {
      const [classResponse, resultsResponse] = await Promise.all([
        fetchJson(urls.classes), fetchJson(urls.results),
      ]);
      if (!classResponse.ok || !resultsResponse.ok) throw new Error('load_failed');
      const [classData, resultData] = await Promise.all([classResponse.json(), resultsResponse.json()]);
      const allowedClasses = (Array.isArray(classData) ? classData : []).map(item => (
        typeof item === 'string' ? { id: item, name: item } :
          { id: item?.id, name: String(item?.name || '') }
      )).filter(item => item.id !== undefined && item.id !== null && item.name)
        .sort((a,b) => a.name.localeCompare(b.name,'tr-TR'));
      setRoster([]);
      setRosterError(false);
      setClasses(allowedClasses);
      setResults(Array.isArray(resultData?.items) ? resultData.items : []);
      setClassId(current => (
        allowedClasses.some(item => String(item.id) === String(current))
          ? String(current) : allowedClasses.length === 1 ? String(allowedClasses[0].id) : ''
      ));
    } catch (err) {
      setLoadError('Ders değerlendirmeleri yüklenemedi.');
      onError?.('Ders değerlendirmeleri yüklenemedi.');
    } finally {
      setLoading(false);
    }
  }, [schoolId, urls, onError]);

  useEffect(() => { load(); }, [load]);

  // The existing authorized point-roster endpoint already supplies active avatar selections.
  // Read only avatar metadata; if unavailable, StudentAvatarView uses its stable animal fallback.
  useEffect(() => {
    if (!schoolId) return undefined;
    let cancelled = false;
    setAvatarSelections({});
    fetchJson(urls.avatarRoster)
      .then(res => (res.ok ? res.json() : null))
      .then(data => {
        if (cancelled || !Array.isArray(data?.students)) return;
        const map = {};
        data.students.forEach(row => {
          if (row?.student_id != null) map[String(row.student_id)] = {
            avatar_id: row.avatar_id || null, starter_emoji: row.starter_emoji || null,
          };
        });
        setAvatarSelections(map);
      })
      .catch(() => { /* Avatar failure must not block class results. */ });
    return () => { cancelled = true; };
  }, [schoolId, urls]);

  const chosenClass = classes.find(item => String(item.id) === String(classId)) || null;
  useEffect(() => {
    if (!schoolId || !chosenClass) { setRoster([]); return; }
    let cancelled = false;
    setRoster([]);
    setRosterError(false);
    setLoadingRoster(true);
    fetchJson(urls.students(chosenClass.id))
      .then(response => { if (!response.ok) throw new Error('students_failed'); return response.json(); })
      .then(data => { if (!cancelled) setRoster(Array.isArray(data) ? data : []); })
      .catch(() => { if (!cancelled) { setRoster([]); setRosterError(true); onError?.('Sınıfın öğrenci listesi yüklenemedi.'); } })
      .finally(() => { if (!cancelled) setLoadingRoster(false); });
    return () => { cancelled = true; };
  }, [schoolId, chosenClass?.id, urls, onError]); // eslint-disable-line react-hooks/exhaustive-deps

  const classRows = useMemo(() => results.filter(row => row.class_name === chosenClass?.name), [results, chosenClass?.name]);
  const examOptions = useMemo(() => {
    const unique = new Map();
    classRows.forEach(row => {
      const id = String(row.exam_id ?? '');
      if (id && !unique.has(id)) unique.set(id, { id, label: `${row.exam_title || 'Değerlendirme'} · ${row.lesson_name || 'Ders yok'} · ${classPulseDate(row.exam_date)}` });
    });
    return [...unique.values()];
  }, [classRows]);
  const filteredRows = useMemo(() => (
    examFilter === 'all' ? classRows : classRows.filter(row => String(row.exam_id) === examFilter)
  ), [classRows, examFilter]);
  const asOf = useMemo(() => new Date().toLocaleDateString('sv-SE', { timeZone: 'Europe/Istanbul' }), []);
  const periodRows = useMemo(() => filteredRows.filter(row => withinAssessmentPeriod(row.exam_date, period, asOf)),
    [filteredRows, period, asOf]);
  const enrichedRoster = useMemo(() => roster.map(student => ({
    ...student, ...(avatarSelections[String(student.id ?? student.student_id)] || {}),
  })), [roster, avatarSelections]);
  const pulse = useMemo(() => buildClassPulse(periodRows, enrichedRoster), [periodRows, enrichedRoster]);
  const namedSubjects = useMemo(() => pulse.subjects.filter(subject => subject.key !== 'unassigned'), [pulse.subjects]);
  const visibleStudents = useMemo(() => {
    const term = search.toLocaleLowerCase('tr-TR').trim();
    const match = pulse.students.filter(student => student.name.toLocaleLowerCase('tr-TR').includes(term));
    if (sort === 'high') return match.sort((a,b) => (b.average ?? -1) - (a.average ?? -1) || a.name.localeCompare(b.name,'tr-TR'));
    if (sort === 'low') return match.sort((a,b) => (a.average ?? 101) - (b.average ?? 101) || a.name.localeCompare(b.name,'tr-TR'));
    return match;
  }, [pulse.students, search, sort]);
  const selectedStudent = pulse.students.find(item => item.id === studentId) || null;
  const selectedSubject = useMemo(() => {
    const subject = namedSubjects.find(item => item.key === subjectId);
    return subject ? buildClassPulseSubjectDetail(subject, periodRows, pulse.students) : null;
  }, [subjectId, namedSubjects, periodRows, pulse.students]);
  useEffect(() => { setSubjectId(null); }, [classId, examFilter, period]);
  const statusCounts = useMemo(() => notificationCounts(periodRows), [periodRows]);

  if (loading) {
    return <Box role="status" aria-label="Ders değerlendirmeleri yükleniyor" sx={{ py: 7, textAlign: 'center' }}>
      <CircularProgress /><Typography sx={{ mt: 2 }}>Ders değerlendirmeleri yükleniyor…</Typography>
    </Box>;
  }
  if (loadError) {
    return <Alert severity="error" action={
      <Button onClick={load} sx={actionSx}>Yeniden Dene</Button>
    }>{loadError}</Alert>;
  }

  const hasClass = Boolean(chosenClass);
  const periodDescription = period === 'all' ? 'Tüm değerlendirmeler' : period === 'last30' ? 'Son 30 gün' : 'Son 90 gün';
  return (
    <Box sx={{ pb: 2, minWidth: 0, fontSynthesis: 'none' }}>
      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between"
        alignItems={{ md: 'flex-start' }} spacing={2.5} sx={{ mb: 2.5 }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h5" component="h1" sx={teacherTypography.pageTitle}>
            Sınıfın Nabzı
          </Typography>
          <Typography variant="body2" sx={{ ...teacherTypography.supportingText, fontSize: 14, mt: 0.7 }}>
            Öğrencilerinin ders sonuçlarını ve kendi gelişimlerini bir bakışta incele.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ width: { xs: '100%', md: 'auto' } }}>
          {classes.length === 1 && (
            <Chip label={`${classes[0].name} Sınıfı`} variant="outlined"
              sx={{ height: 44, bgcolor: '#f5f1ff', borderColor: '#ded5ff', color: '#5843b3', fontWeight: 600 }} />
          )}
          {classes.length > 1 && (
            <FormControl size="small" sx={{ minWidth: 110 }}>
              <Select aria-label="Şube" inputProps={{ 'aria-label': 'Şube' }} displayEmpty value={classId}
                onChange={event => { setRoster([]); setLoadingRoster(true); setClassId(event.target.value); setExamFilter('all'); setStudentId(null); }}
                sx={{ ...selectTouchSx, bgcolor: '#fff' }}>
                <MenuItem value="">Şube seç</MenuItem>
                {classes.map(item => <MenuItem key={item.id} value={String(item.id)}>{item.name}</MenuItem>)}
              </Select>
            </FormControl>
          )}
          <FormControl size="small" sx={{ minWidth: { xs: 132, sm: 158 } }}>
            <Select aria-label="Değerlendirme dönemi" inputProps={{ 'aria-label': 'Değerlendirme dönemi' }} value={period} sx={{ ...selectTouchSx, bgcolor: '#fff' }}
              onChange={event => { setPeriod(event.target.value); setStudentId(null); }}>
              <MenuItem value="all">Tüm değerlendirmeler</MenuItem>
              <MenuItem value="last30">Son 30 gün</MenuItem>
              <MenuItem value="last90">Son 90 gün</MenuItem>
            </Select>
          </FormControl>
          <Button variant="contained" startIcon={<AddIcon />} disabled={!onCreateAssessment}
            onClick={onCreateAssessment} sx={{ ...actionSx, bgcolor: '#6546BF', '&:hover': { bgcolor: '#5336AA' }, flex: { xs: '0 0 100%', sm: 'initial' }, width: { xs: '100%', sm: 'auto' } }}>
            Yeni Değerlendirme
          </Button>
        </Stack>
      </Stack>

      {!hasClass ? (
        <EmptyState title={classes.length ? 'Şube seçin' : 'Henüz erişilebilir şube yok'}>
          {classes.length ? 'Sınıfın sonuçlarını görmek için yukarıdan bir şube seçin.' : 'Sınıf yetkiniz tanımlandıktan sonra bu ekranı kullanabilirsiniz.'}
        </EmptyState>
      ) : loadingRoster ? (
        <Box role="status" sx={{ py: 5, textAlign: 'center' }}><CircularProgress size={28} /> <Typography>Öğrenciler yükleniyor…</Typography></Box>
      ) : rosterError ? (
        <Alert severity="error" action={<Button onClick={load} sx={actionSx}>Yeniden Dene</Button>}>
          Sınıfın öğrenci listesi alınamadı. Eksik veriyle ortalama gösterilmiyor.
        </Alert>
      ) : (
        <>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2,minmax(0,1fr))', md: 'repeat(4,minmax(0,1fr))' },
            gap: { xs: 1, md: 1.5 }, mb: 2.5,
            '@media (max-width: 359px)': { gap: 1 } }}>
            <KpiCard density="report" emphasis label="Genel ortalama" value={classPulseFormat(pulse.overall)} help={`${pulse.numericStudents} öğrenciden puanlı sonuç`} icon={BarChartOutlinedIcon} />
            <KpiCard density="report" label="Öğrenci" value={String(pulse.totalStudents)} help="sınıfta" icon={PeopleOutlineIcon} />
            <KpiCard density="report" label="Ders" value={String(pulse.subjects.filter(s => s.key !== 'unassigned').length)}
              help="değerlendirmesi olan" icon={MenuBookOutlinedIcon} />
            <KpiCard density="report" label="Son değerlendirme" value={pulse.lastDate ? classPulseDate(pulse.lastDate) : '—'}
              help={periodDescription} icon={EventOutlinedIcon} valueKind="text" />
          </Box>

          <Paper sx={{ ...surface, p: { xs: 2, md: 2.5 }, mb: 2.5 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between"
              alignItems={{ sm: 'center' }} spacing={1} sx={{ mb: 2 }}>
              <TeacherSectionHeader title="Ders Performansı" />
              <Typography variant="caption" sx={teacherTypography.metadata}>
                Öğrenci başına ders ortalamaları eşit ağırlıklı
              </Typography>
            </Stack>
            {namedSubjects.length ? (
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2,minmax(0,1fr))',
                lg: `repeat(${Math.min(4, namedSubjects.length)}, minmax(0,1fr))` }, gap: 1.2 }}>
                {namedSubjects.map(subject => (
                  <SubjectCard key={subject.key} subject={subject}
                    onSelect={(key) => { setStudentId(null); setSubjectId(key); }} />
                ))}
              </Box>
            ) : (
              <EmptyState title="Henüz ders sonucu yok">Bu şubede bu dönem için puanı girilmiş ders değerlendirmesi bulunmuyor.</EmptyState>
            )}
            {pulse.unassignedCount > 0 && (
              <Typography variant="caption" sx={{ ...teacherTypography.metadata, display: 'block', mt: 1.5 }}>
                Ders belirtilmemiş {pulse.unassignedCount} sonuç genel ortalamaya dahildir; ders kartlarında gösterilmez.
              </Typography>
            )}
          </Paper>

          <DevelopmentAssessmentDistribution summary={pulse.development} onSelectStudent={setStudentId} />

          {pulse.insights.length > 0 && (
            <Paper sx={{ ...surface, p: { xs: 2, md: 2.5 }, mb: 2.5 }}>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.8 }}>
                <AutoAwesomeOutlinedIcon fontSize="small" sx={{ color: '#7354d9' }} />
                <TeacherSectionHeader title="Destek Fırsatları" />
              </Stack>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2,minmax(0,1fr))', lg: 'repeat(4,minmax(0,1fr))' }, gap: 1 }}>
                {pulse.insights.map(insight => {
                  const student = pulse.students.find(item => item.id === insight.studentId);
                  if (!student) return null;
                  return (
                    <Button key={student.id} variant="outlined" color="inherit"
                      onClick={() => setStudentId(student.id)} aria-label={`${student.name} öğrenci detayını aç`}
                      sx={{ ...actionSx, textAlign: 'left', justifyContent: 'flex-start',
                        alignItems: 'flex-start', borderRadius: 3, p: 1.5,
                        borderColor: '#e7e3f6', bgcolor: '#fff',
                        boxShadow: '0 3px 10px rgba(67,55,125,.02)',
                        '&:hover': { bgcolor: '#f8f5ff', borderColor: '#bdb0ee' } }}>
                      <Stack direction="row" spacing={1.2} sx={{ minWidth: 0 }}>
                        <StudentAvatarView studentId={student.id} avatarId={student.avatarId}
                          starterEmoji={student.starterEmoji} name={student.name} size={42} />
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="body2" sx={teacherTypography.studentName}>{student.name}</Typography>
                          <Typography variant="caption" sx={{ ...teacherTypography.metadata, display: 'block', mt: 0.5, lineHeight: 1.5, whiteSpace: 'normal' }}>
                            {insight.text}
                          </Typography>
                        </Box>
                      </Stack>
                    </Button>
                  );
                })}
              </Box>
            </Paper>
          )}

          {pulse.pendingStudents.length > 0 && pulse.exams.length > 0 && (
            <Paper sx={{ ...surface, p: { xs: 2, md: 2.5 }, mb: 2.5 }}>
              <TeacherSectionHeader title="Sonucu Bekleyenler" />
              <Typography variant="caption" sx={{ ...teacherTypography.metadata, display: 'block', mt: 0.5, mb: 1.5 }}>
                Bu öğrenciler için henüz puan veya gelişim düzeyi sonucu yok; bu bir başarı değerlendirmesi değildir.
              </Typography>
              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                {pulse.pendingStudents.slice(0, 8).map(student => (
                  <Button key={student.id} variant="outlined" color="inherit" onClick={() => setStudentId(student.id)}
                    sx={{ ...actionSx, borderRadius: 2, gap: 1, borderColor: '#e8eaf3' }}
                    aria-label={`${student.name} detayını gör`}>
                    <StudentAvatarView studentId={student.id} avatarId={student.avatarId}
                      starterEmoji={student.starterEmoji} name={student.name} size={34} />
                    {student.name}
                  </Button>
                ))}
              </Stack>
            </Paper>
          )}

          <Paper sx={{ ...surface, p: { xs: 2, md: 2.5 }, mb: 2.5 }}>
            <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ md: 'center' }}
              justifyContent="space-between" spacing={1.5} sx={{ mb: 2 }}>
              <TeacherSectionHeader title="Öğrenci Listesi" />
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ width: { xs: '100%', md: 'auto' } }}>
                <TextField size="small" label="Öğrenci ara" value={search} onChange={event => setSearch(event.target.value)}
                  sx={{ minWidth: { sm: 210 }, '& .MuiInputBase-root': { minHeight: 44 } }}
                  InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} />
                <FormControl size="small">
                  <Select value={sort} aria-label="Öğrenci sıralaması" inputProps={{ 'aria-label': 'Öğrenci sıralaması' }} onChange={event => setSort(event.target.value)}
                    sx={{ ...selectTouchSx, minWidth: 170 }}>
                    <MenuItem value="alpha">Ada göre (A-Z)</MenuItem>
                    <MenuItem value="high">Ortalama: yüksekten</MenuItem>
                    <MenuItem value="low">Ortalama: düşükten</MenuItem>
                  </Select>
                </FormControl>
              </Stack>
            </Stack>
            {visibleStudents.length === 0 ? (
              <EmptyState title={pulse.students.length ? 'Eşleşen öğrenci yok' : 'Henüz öğrenci yok'}>
                {pulse.students.length ? 'Arama metnini değiştirin.' : 'Bu sınıfa öğrenci eklendiğinde burada görüntülenir.'}
              </EmptyState>
            ) : (
              <>
                <Box sx={{ display: { xs: 'block', md: 'none' } }}>
                  {visibleStudents.map(student => <TeacherStudentResultRow key={student.id} student={student} value={student.average}
                    metadata={student.average === null ? (student.developmentCount ? undefined :
                      student.records.length > 0 && student.records.every(record => record.status === 'absent') ? 'Girmedi' : 'Puanlı sonuç bekleniyor') : student.subjectCount + ' ders sonucu'}
                    summary={student.developmentCount ? 'Düzey sonucu · ' + student.developmentCount + ' kayıt' : null}
                    actionLabel={student.name + ' detayını gör'} onClick={() => setStudentId(student.id)} />)}
                </Box>
                <TableContainer sx={{ display: { xs: 'none', md: 'block' }, maxHeight: 570, overflowX: 'auto' }}>
                  <Table stickyHeader size="small" aria-label="Öğrenci ders değerlendirme ortalamaları">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ ...teacherTypography.tableHeader, minWidth: 195 }}>Öğrenci</TableCell>
                        <TableCell align="center" sx={{ ...teacherTypography.tableHeader, minWidth: 110 }}>Genel Ort.</TableCell>
                        {pulse.subjects.filter(s=>s.key!=='unassigned').map(subject => (
                          <TableCell align="center" sx={{ ...teacherTypography.tableHeader, minWidth: 100 }} key={subject.key}>{subject.label}</TableCell>
                        ))}
                        <TableCell sx={{ minWidth: 94 }} />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {visibleStudents.map(student => (
                        <TableRow hover key={student.id}>
                          <TableCell>
                            <TeacherStudentIdentity student={student} />
                          </TableCell>
                          <TableCell align="center" sx={teacherTypography.kpiValue}>
                            {student.average === null && student.developmentCount ? 'Gelişim Düzeyi' : classPulseFormat(student.average)}
                          </TableCell>
                          {pulse.subjects.filter(s=>s.key!=='unassigned').map(subject => (
                            <TableCell key={subject.key} align="center">
                              {!Number.isFinite(student.subjectAverages[subject.key]) && student.records.some(r => r.subjectKey === subject.key && r.developmentLevel) ? null : classPulseFormat(student.subjectAverages[subject.key])}
                              {student.records.some(r => r.subjectKey === subject.key && r.developmentLevel) &&
                                <Typography variant="caption" display="block">Gelişim Düzeyi</Typography>}
                            </TableCell>
                          ))}
                          <TableCell>
                            <Button size="small" onClick={() => setStudentId(student.id)} sx={actionSx}
                              aria-label={`${student.name} detayını gör`}>Detay <ChevronRightIcon fontSize="small" /></Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}
          </Paper>

          <Paper sx={{ ...surface, p: { xs: 2, md: 2.5 } }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ sm: 'center' }}
              justifyContent="space-between" spacing={1} sx={{ mb: 1.5 }}>
              <TeacherSectionHeader title="Değerlendirme Geçmişi" />
              <FormControl size="small" sx={{ minWidth: { sm: 235 } }}>
                <Select aria-label="Değerlendirmeye göre filtrele" inputProps={{ 'aria-label': 'Değerlendirmeye göre filtrele' }} value={examFilter}
                  onChange={event => { setExamFilter(event.target.value); setStudentId(null); }}
                  sx={selectTouchSx}>
                  <MenuItem value="all">Tüm değerlendirmeler</MenuItem>
                  {examOptions.map(exam => <MenuItem key={exam.id} value={exam.id}>{exam.label}</MenuItem>)}
                </Select>
              </FormControl>
            </Stack>
            {pulse.exams.length ? (
              <Stack spacing={0}>
                {pulse.exams.slice(0, 18).map(exam => (
                  <TeacherAssessmentHistoryRow key={exam.id} title={exam.title.startsWith(exam.subject) ? exam.title : exam.title + ' · ' + exam.subject}
                    dateLabel={classPulseDate(exam.date)} metadata={exam.completed + (exam.scoringMode === 'development' ? ' düzey sonucu' : ' puanlı') + ' · ' + exam.absent + ' girmedi'}
                    value={exam.scoringMode === 'development' ? null : exam.average}
                    summary={exam.scoringMode === 'development' ? 'Düzey sonucu' : null} />
                ))}
              </Stack>
            ) : (
              <EmptyState title="Değerlendirme sonucu bulunamadı">Başka bir dönem veya değerlendirme seçebilirsiniz.</EmptyState>
            )}
            <Typography variant="caption" sx={{ ...teacherTypography.metadata, display: 'block', mt: 1.5 }}>
              Seçili kapsam bildirimleri: {statusCounts.sent} gönderildi · {statusCounts.pending} kuyrukta · {statusCounts.unshared} paylaşım bekliyor
            </Typography>
            <Stack direction="row" justifyContent="flex-end" sx={{ mt: 1 }}>
              <Button onClick={load} startIcon={<RefreshIcon />} sx={actionSx}>Yenile</Button>
            </Stack>
          </Paper>
        </>
      )}
      <ClassPulseStudentDrawer student={selectedStudent} students={visibleStudents}
        className={chosenClass?.name || ''} onClose={() => setStudentId(null)} onSelect={setStudentId} />
      <ClassPulseSubjectDrawer subject={selectedSubject}
        className={chosenClass?.name || ''} onClose={() => setSubjectId(null)} />
    </Box>
  );
}
